import React, { useState } from 'react';
import api from '../utils/api';
import { 
  Home, Building2, Calculator, TrendingUp, DollarSign, 
  Plus, Trash2, BarChart3, ArrowRight, Lock, Crown,
  ChevronDown, ChevronUp, Info, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';



const AdvancedHELOC = ({ user, mortgage }) => {
  const [activeSection, setActiveSection] = useState('multi-property');
  const [loading, setLoading] = useState(false);
  
  // Multi-property state
  const [properties, setProperties] = useState([
    { name: 'Primary Home', home_value: mortgage?.home_value || '', current_balance: mortgage?.current_balance || '', primary_residence: 'yes' }
  ]);
  const [multiPropertyResults, setMultiPropertyResults] = useState(null);
  
  // Repayment scenario state
  const [repaymentInput, setRepaymentInput] = useState({
    heloc_amount: 50000,
    heloc_rate: 5.5,
    repayment_years: 10,
    extra_monthly_payment: 0
  });
  const [repaymentResults, setRepaymentResults] = useState(null);
  
  // HELOC vs Refinance state
  const [compareInput, setCompareInput] = useState({
    home_value: mortgage?.home_value || 500000,
    current_balance: mortgage?.current_balance || 300000,
    current_rate: mortgage?.current_rate || 4.5,
    heloc_amount: 50000,
    new_refinance_rate: null,
    years_remaining: mortgage?.amortization_remain || 20
  });
  const [compareResults, setCompareResults] = useState(null);
  
  const isPro = user?.subscription_tier === 'pro';
  
  const addProperty = () => {
    if (properties.length >= 5) {
      toast.error('Maximum 5 properties allowed');
      return;
    }
    setProperties([...properties, { name: `Property ${properties.length + 1}`, home_value: '', current_balance: '', primary_residence: 'no' }]);
  };
  
  const removeProperty = (index) => {
    if (properties.length === 1) return;
    setProperties(properties.filter((_, i) => i !== index));
  };
  
  const updateProperty = (index, field, value) => {
    const updated = [...properties];
    updated[index][field] = value;
    setProperties(updated);
  };
  
  const analyzeMultiProperty = async () => {
    if (!isPro) return;
    
    const validProperties = properties.filter(p => p.home_value && p.current_balance);
    if (validProperties.length === 0) {
      toast.error('Please enter property values');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post(
        `/api/heloc-advanced/multi-property`,
        validProperties
      );
      setMultiPropertyResults(response.data);
      toast.success('Analysis complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };
  
  const calculateRepayment = async () => {
    if (!isPro) return;
    
    setLoading(true);
    try {
      const response = await api.post(
        `/api/heloc-advanced/repayment-scenarios`,
        repaymentInput
      );
      setRepaymentResults(response.data);
      toast.success('Scenarios calculated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };
  
  const compareOptions = async () => {
    if (!isPro) return;
    
    setLoading(true);
    try {
      const response = await api.post(
        `/api/heloc-advanced/vs-refinance`,
        compareInput
      );
      setCompareResults(response.data);
      toast.success('Comparison complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(val);
  };
  
  if (!isPro) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/10 rounded-full mb-4">
          <Crown className="h-8 w-8 text-purple-400" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Advanced HELOC Calculator</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Unlock multi-property analysis, detailed repayment scenarios, and HELOC vs refinance comparisons with Pro.
        </p>
        <a
          href="/dashboard?tab=settings"
          className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
        >
          <Lock className="h-4 w-4" />
          Upgrade to Pro
        </a>
      </div>
    );
  }
  
  return (
    <div data-testid="advanced-heloc" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-heading font-semibold flex items-center gap-2">
          <Calculator className="h-6 w-6 text-purple-400" />
          Advanced HELOC Calculator
          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Pro</span>
        </h3>
      </div>
      
      {/* Section Tabs */}
      <div className="flex gap-2 p-1 bg-muted/30 rounded-lg">
        {[
          { id: 'multi-property', label: 'Multi-Property', icon: Building2 },
          { id: 'repayment', label: 'Repayment Scenarios', icon: BarChart3 },
          { id: 'compare', label: 'HELOC vs Refinance', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === tab.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Multi-Property Analysis */}
      {activeSection === 'multi-property' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Compare HELOC Across Properties
            </h4>
            
            <div className="space-y-4">
              {properties.map((prop, index) => (
                <div key={prop.id || `prop-${index}`} className="p-4 bg-muted/20 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <input
                      type="text"
                      value={prop.name}
                      onChange={(e) => updateProperty(index, 'name', e.target.value)}
                      className="text-sm font-medium bg-transparent border-b border-border focus:border-primary outline-none"
                    />
                    {properties.length > 1 && (
                      <button
                        onClick={() => removeProperty(index)}
                        className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Home Value ($)</label>
                      <input
                        type="number"
                        value={prop.home_value}
                        onChange={(e) => updateProperty(index, 'home_value', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        placeholder="500000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Mortgage Balance ($)</label>
                      <input
                        type="number"
                        value={prop.current_balance}
                        onChange={(e) => updateProperty(index, 'current_balance', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                        placeholder="300000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Property Type</label>
                      <select
                        value={prop.primary_residence}
                        onChange={(e) => updateProperty(index, 'primary_residence', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      >
                        <option value="yes">Primary Residence</option>
                        <option value="no">Investment Property</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="flex gap-3">
                <button
                  onClick={addProperty}
                  disabled={properties.length >= 5}
                  className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  Add Property
                </button>
                <button
                  onClick={analyzeMultiProperty}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Analyzing...' : 'Analyze All Properties'}
                </button>
              </div>
            </div>
          </div>
          
          {/* Multi-Property Results */}
          {multiPropertyResults && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h4 className="text-lg font-semibold">Analysis Results</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/20 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Total Equity</p>
                  <p className="text-xl font-bold text-primary">{formatCurrency(multiPropertyResults.summary.total_equity)}</p>
                </div>
                <div className="p-4 bg-muted/20 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Total HELOC Available</p>
                  <p className="text-xl font-bold text-green-400">{formatCurrency(multiPropertyResults.summary.total_heloc_available)}</p>
                </div>
                <div className="p-4 bg-muted/20 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Eligible Properties</p>
                  <p className="text-xl font-bold">{multiPropertyResults.summary.eligible_properties}/{multiPropertyResults.summary.total_properties}</p>
                </div>
                <div className="p-4 bg-muted/20 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Est. HELOC Rate</p>
                  <p className="text-xl font-bold">{multiPropertyResults.summary.heloc_rate}%</p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3">Property</th>
                      <th className="text-right py-2 px-3">Value</th>
                      <th className="text-right py-2 px-3">Equity</th>
                      <th className="text-right py-2 px-3">LTV</th>
                      <th className="text-right py-2 px-3">Max HELOC</th>
                      <th className="text-center py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {multiPropertyResults.properties.map((prop, idx) => (
                      <tr key={prop.name || `row-${idx}`} className="border-b border-border/50">
                        <td className="py-2 px-3 font-medium">{prop.name}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(prop.home_value)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(prop.equity)}</td>
                        <td className="py-2 px-3 text-right font-mono">{prop.ltv_ratio}%</td>
                        <td className="py-2 px-3 text-right font-mono text-primary">{formatCurrency(prop.max_heloc_available)}</td>
                        <td className="py-2 px-3 text-center">
                          {prop.eligible ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Eligible</span>
                          ) : (
                            <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">Limited</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {multiPropertyResults.recommendations?.length > 0 && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <h5 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4 text-primary" />
                    Recommendations
                  </h5>
                  <ul className="space-y-1 text-sm">
                    {multiPropertyResults.recommendations.map((rec) => (
                      <li key={rec} className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Repayment Scenarios */}
      {activeSection === 'repayment' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              HELOC Repayment Scenarios
            </h4>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">HELOC Amount ($)</label>
                <input
                  type="number"
                  value={repaymentInput.heloc_amount}
                  onChange={(e) => setRepaymentInput({...repaymentInput, heloc_amount: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Interest Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={repaymentInput.heloc_rate}
                  onChange={(e) => setRepaymentInput({...repaymentInput, heloc_rate: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Repayment Years</label>
                <input
                  type="number"
                  value={repaymentInput.repayment_years}
                  onChange={(e) => setRepaymentInput({...repaymentInput, repayment_years: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Extra Monthly ($)</label>
                <input
                  type="number"
                  value={repaymentInput.extra_monthly_payment}
                  onChange={(e) => setRepaymentInput({...repaymentInput, extra_monthly_payment: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  placeholder="0"
                />
              </div>
            </div>
            
            <button
              onClick={calculateRepayment}
              disabled={loading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Calculating...' : 'Calculate Scenarios'}
            </button>
          </div>
          
          {/* Repayment Results */}
          {repaymentResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Interest Only */}
                <div className="bg-card border border-border rounded-xl p-5">
                  <h5 className="font-semibold mb-3 text-yellow-400">Interest Only</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monthly Payment</span>
                      <span className="font-mono font-bold">{formatCurrency(repaymentResults.scenarios.interest_only.monthly_payment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Interest</span>
                      <span className="font-mono text-red-400">{formatCurrency(repaymentResults.scenarios.interest_only.total_interest)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payoff</span>
                      <span className="font-mono">{repaymentResults.scenarios.interest_only.payoff_years} years</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">{repaymentResults.scenarios.interest_only.note}</p>
                </div>
                
                {/* Principal + Interest */}
                <div className="bg-card border border-border rounded-xl p-5">
                  <h5 className="font-semibold mb-3 text-blue-400">Principal + Interest</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monthly Payment</span>
                      <span className="font-mono font-bold">{formatCurrency(repaymentResults.scenarios.principal_interest.monthly_payment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Interest</span>
                      <span className="font-mono text-red-400">{formatCurrency(repaymentResults.scenarios.principal_interest.total_interest)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payoff</span>
                      <span className="font-mono">{repaymentResults.scenarios.principal_interest.payoff_years} years</span>
                    </div>
                  </div>
                </div>
                
                {/* Accelerated */}
                <div className="bg-card border-2 border-primary rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <h5 className="font-semibold text-primary">Accelerated</h5>
                    <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">Best</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monthly Payment</span>
                      <span className="font-mono font-bold">{formatCurrency(repaymentResults.scenarios.accelerated.monthly_payment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Interest</span>
                      <span className="font-mono text-green-400">{formatCurrency(repaymentResults.scenarios.accelerated.total_interest)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payoff</span>
                      <span className="font-mono">{repaymentResults.scenarios.accelerated.payoff_years} years</span>
                    </div>
                    {repaymentResults.scenarios.accelerated.interest_saved > 0 && (
                      <div className="pt-2 border-t border-border">
                        <p className="text-green-400 font-semibold">
                          Save {formatCurrency(repaymentResults.scenarios.accelerated.interest_saved)} in interest!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Amortization Chart */}
              {repaymentResults.amortization_preview && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <h5 className="font-semibold mb-4">Amortization Preview</h5>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={repaymentResults.amortization_preview}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="year" stroke="#888" />
                      <YAxis stroke="#888" tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                        formatter={(val) => formatCurrency(val)}
                      />
                      <Legend />
                      <Bar dataKey="yearly_principal" name="Principal" fill="#10b981" stackId="a" />
                      <Bar dataKey="yearly_interest" name="Interest" fill="#ef4444" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* HELOC vs Refinance */}
      {activeSection === 'compare' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              HELOC vs Cash-Out Refinance
            </h4>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Home Value ($)</label>
                <input
                  type="number"
                  value={compareInput.home_value}
                  onChange={(e) => setCompareInput({...compareInput, home_value: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Current Balance ($)</label>
                <input
                  type="number"
                  value={compareInput.current_balance}
                  onChange={(e) => setCompareInput({...compareInput, current_balance: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Current Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={compareInput.current_rate}
                  onChange={(e) => setCompareInput({...compareInput, current_rate: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Cash Needed ($)</label>
                <input
                  type="number"
                  value={compareInput.heloc_amount}
                  onChange={(e) => setCompareInput({...compareInput, heloc_amount: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Refinance Rate (%) <span className="text-muted-foreground/60">optional</span></label>
                <input
                  type="number"
                  step="0.1"
                  value={compareInput.new_refinance_rate || ''}
                  onChange={(e) => setCompareInput({...compareInput, new_refinance_rate: e.target.value ? parseFloat(e.target.value) : null})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                  placeholder="Auto-estimate"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Years Remaining</label>
                <input
                  type="number"
                  value={compareInput.years_remaining}
                  onChange={(e) => setCompareInput({...compareInput, years_remaining: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                />
              </div>
            </div>
            
            <button
              onClick={compareOptions}
              disabled={loading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Comparing...' : 'Compare Options'}
            </button>
          </div>
          
          {/* Comparison Results */}
          {compareResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* HELOC Option */}
                <div className={`bg-card border-2 rounded-xl p-6 ${compareResults.recommendation.choice === 'heloc' ? 'border-primary' : 'border-border'}`}>
                  {compareResults.recommendation.choice === 'heloc' && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary text-xs rounded-full mb-3">
                      <CheckCircle className="h-3 w-3" />
                      Recommended
                    </div>
                  )}
                  <h5 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Home className="h-5 w-5 text-blue-400" />
                    HELOC Option
                  </h5>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Keep Mortgage Payment</span>
                      <span className="font-mono">{formatCurrency(compareResults.heloc_option.current_mortgage_payment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">+ HELOC Interest</span>
                      <span className="font-mono">{formatCurrency(compareResults.heloc_option.heloc_interest_payment)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t border-border pt-2">
                      <span>Total Monthly</span>
                      <span className="font-mono text-lg">{formatCurrency(compareResults.heloc_option.total_monthly_payment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">HELOC Rate</span>
                      <span className="font-mono">{compareResults.heloc_option.heloc_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Upfront Costs</span>
                      <span className="font-mono text-green-400">$0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">5-Year Cost</span>
                      <span className="font-mono">{formatCurrency(compareResults.heloc_option.five_year_cost)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-semibold text-green-400 mb-2">Pros:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {compareResults.heloc_option.pros.map((pro) => (
                        <li key={pro}>✓ {pro}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Refinance Option */}
                <div className={`bg-card border-2 rounded-xl p-6 ${compareResults.recommendation.choice === 'refinance' ? 'border-primary' : 'border-border'}`}>
                  {compareResults.recommendation.choice === 'refinance' && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary text-xs rounded-full mb-3">
                      <CheckCircle className="h-3 w-3" />
                      Recommended
                    </div>
                  )}
                  <h5 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-purple-400" />
                    Refinance Option
                  </h5>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between font-bold">
                      <span>New Monthly Payment</span>
                      <span className="font-mono text-lg">{formatCurrency(compareResults.refinance_option.new_mortgage_payment)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">New Rate</span>
                      <span className="font-mono">{compareResults.refinance_option.new_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">New Balance</span>
                      <span className="font-mono">{formatCurrency(compareResults.refinance_option.new_balance)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Upfront Costs</span>
                      <span className="font-mono text-red-400">{formatCurrency(compareResults.refinance_option.upfront_costs)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">5-Year Cost</span>
                      <span className="font-mono">{formatCurrency(compareResults.refinance_option.five_year_cost)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-semibold text-green-400 mb-2">Pros:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {compareResults.refinance_option.pros.map((pro) => (
                        <li key={pro}>✓ {pro}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Recommendation */}
              <div className="bg-primary/10 border border-primary/20 rounded-xl p-6">
                <h5 className="font-semibold mb-2 flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  Our Recommendation
                </h5>
                <p className="text-sm">{compareResults.recommendation.reason}</p>
                
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-primary/20">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Monthly Difference</p>
                    <p className="font-bold font-mono">{formatCurrency(compareResults.comparison.monthly_difference)}</p>
                    <p className="text-xs text-primary">{compareResults.comparison.cheaper_monthly} is cheaper</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">5-Year Savings</p>
                    <p className="font-bold font-mono">{formatCurrency(compareResults.comparison.five_year_savings)}</p>
                    <p className="text-xs text-primary">{compareResults.comparison.better_five_year} wins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Break-Even</p>
                    <p className="font-bold font-mono">{compareResults.comparison.breakeven_months} months</p>
                    <p className="text-xs text-muted-foreground">for refinance costs</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedHELOC;
